import { checkIsArrayEmpty } from "../../../../../utils/common/dataTypes/array";
import { checkIsStringEmpty } from "../../../../Common/modules/DataTypes/utils/strings";
import { CUSTOM_QUESTION_TYPES_ID } from "../../../../CustomQuestions/constants/CustomQuestions.constants";
import { IBookingQuestions } from "../IBookingQuestions";

/**
 * Maps each custom question type to its validation logic.
 * Returns "Required" or "Invalid URL" if validation fails, otherwise undefined.
 */
export const customQuestionsValidationMap: Record<
  number,
  (value: unknown, isMandatory?: boolean) => string | undefined
> = {
  [CUSTOM_QUESTION_TYPES_ID.PARAGRAPH_TEXT]: (value, isMandatory) =>
    isMandatory &&
    (typeof value === "string" ? checkIsStringEmpty(value) : true)
      ? "Required"
      : undefined,

  [CUSTOM_QUESTION_TYPES_ID.SHORT_TEXT]: (value, isMandatory) =>
    isMandatory &&
    (typeof value === "string" ? checkIsStringEmpty(value) : true)
      ? "Required"
      : undefined,

  [CUSTOM_QUESTION_TYPES_ID.URL]: (value, isMandatory) => {
    // Required check
    if (
      isMandatory &&
      (typeof value !== "string" || checkIsStringEmpty(value))
    ) {
      return "Required";
    }

    // Validate URL using URL constructor
    if (typeof value === "string" && value) {
      try {
        const url = new URL(value);
        if (!url.protocol.startsWith("http")) {
          return "Invalid URL";
        }
      } catch {
        return "Invalid URL";
      }
    }
    return undefined;
  },

  [CUSTOM_QUESTION_TYPES_ID.NUMBER]: (value, isMandatory) =>
    isMandatory && (value === undefined || value === null || value === "")
      ? "Required"
      : undefined,

  [CUSTOM_QUESTION_TYPES_ID.SINGLE_SELECT_DROPDOWN]: (value, isMandatory) =>
    isMandatory &&
    (typeof value === "string" ? checkIsStringEmpty(value) : true)
      ? "Required"
      : undefined,

  [CUSTOM_QUESTION_TYPES_ID.MULTI_SELECT_DROPDOWN]: (value, isMandatory) =>
    isMandatory && checkIsArrayEmpty(value as []) ? "Required" : undefined,

  [CUSTOM_QUESTION_TYPES_ID.CALENDAR]: (value, isMandatory) =>
    isMandatory &&
    (!value ||
      (typeof value === "object" &&
        value instanceof Date &&
        isNaN(value.getTime())))
      ? "Required"
      : undefined,

  [CUSTOM_QUESTION_TYPES_ID.FILE]: (value, isMandatory) =>
    isMandatory && !value ? "Required" : undefined,

  [CUSTOM_QUESTION_TYPES_ID.GENDER]: (value, isMandatory) =>
    isMandatory &&
    (typeof value === "string" ? checkIsStringEmpty(value) : true)
      ? "Required"
      : undefined,

  [CUSTOM_QUESTION_TYPES_ID.INDIAN_STATE]: (value, isMandatory) =>
    isMandatory &&
    (typeof value === "string" ? checkIsStringEmpty(value) : true)
      ? "Required"
      : undefined,

  [CUSTOM_QUESTION_TYPES_ID.INDIAN_CITY]: (value, isMandatory) =>
    isMandatory &&
    (typeof value === "string" ? checkIsStringEmpty(value) : true)
      ? "Required"
      : undefined,
};

/**
 * Cleans up answers in different formats:
 * - String → trims it
 * - Array of strings → trims each one
 * - Array of IMultiQuestion → trims the `answer` field if it's a string
 */
export function normalizeAnswers(
  data: string | Array<string> | IBookingQuestions.IMultiQuestion[],
): typeof data {
  try {
    // case: plain string
    if (typeof data === "string") {
      return data.trim();
    }

    if (!Array.isArray(data)) {
      return data; // return as-is if neither string nor array
    }

    // case: array of strings
    if (!checkIsArrayEmpty(data) && typeof data[0] === "string") {
      return (data as string[]).map((str) => str.trim());
    }

    // case: array of IMultiQuestion
    return (data as IBookingQuestions.IMultiQuestion[]).map((item) => {
      if ("answer" in item && typeof item.answer === "string") {
        return { ...item, answer: item.answer.trim() };
      }
      return item;
    });
  } catch {
    return data;
  }
}
